/* ============================================================
   RAW & HUNGRY — Page transition (Osmo pixelated wave reveal)
   ------------------------------------------------------------
   A faithful port of the original `pixelGrid()` + `runPageLeaveAnimation()`
   from main.js. The original ran as a Barba `sync` transition, so BOTH the
   outgoing (`current`) and incoming (`next`) containers were on screen at
   once: `next` was fixed-positioned on top and clip-path–revealed in
   `pixelHorizontalAmount` stepped columns, while a column of ember pixels
   flashed in→out along the advancing wipe edge, all above the still-visible
   `current` page.

   Next.js unmounts the outgoing page on navigation, so we reproduce the
   two-page overlap exactly: the caller freezes a clone of the outgoing
   <main> (that clone IS `current`) and the real incoming <main> is `next`.
   Every constant, clip polygon, timing formula, stagger and clearProps below
   is identical to the source — only the container plumbing differs.

   `runPageEnterAnimation`'s role (resolve at `pageReady`, then resetPage +
   initPage) is fulfilled by the `onReady` callback, invoked at exactly
   transitionDuration + transitionEndDelay like the original.
   ============================================================ */

import { gsap } from 'gsap';

// Osmo transition settings — identical to main.js.
const pixelHorizontalAmount = 12;
const transitionDuration = 1.0;
const pixelFadeDuration = 0.2;
const pixelOverlap = 0.3;

export const TRANSITION_END_DELAY = transitionDuration / Math.max(1, pixelHorizontalAmount);

// Handle returned by runPageTransition so the caller can cancel an in-flight
// wipe (StrictMode double-mount, back/forward or locale remount mid-transition).
export type TransitionHandle = { kill: () => void };

const NOOP_HANDLE: TransitionHandle = { kill: () => {} };

function reduceMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Ported verbatim from pixelGrid(): (re)build `pixelHorizontalAmount` columns,
// each filled with enough square pixels to span the panel's cross axis.
function pixelGrid(isPortrait: boolean): void {
  const panel = document.querySelector<HTMLElement>('[data-transition-panel]');
  if (!panel) return;

  const rect = panel.getBoundingClientRect();
  panel.style.flexDirection = isPortrait ? 'column' : 'row';

  const lineSizePx = isPortrait ? rect.height / pixelHorizontalAmount : rect.width / pixelHorizontalAmount;
  const crossAmount = Math.ceil((isPortrait ? rect.width : rect.height) / lineSizePx);

  let lines = panel.querySelectorAll<HTMLElement>('[data-transition-col]');
  const lineTemplate = lines[0];
  if (!lineTemplate) return;
  const pixelTemplate = lineTemplate.querySelector<HTMLElement>('[data-transition-pixel]');
  if (!pixelTemplate) return;

  if (lines.length !== pixelHorizontalAmount) {
    const frag = document.createDocumentFragment();
    for (let i = 0; i < pixelHorizontalAmount; i++) frag.appendChild(lineTemplate.cloneNode(false));
    panel.replaceChildren(frag);
    lines = panel.querySelectorAll<HTMLElement>('[data-transition-col]');
  }

  lines.forEach((line) => {
    line.style.flexDirection = isPortrait ? 'row' : 'column';
    line.style.flex = '1 1 auto';
    line.style.justifyContent = 'center';

    const diff = crossAmount - line.childElementCount;
    if (diff > 0) {
      const frag = document.createDocumentFragment();
      for (let i = 0; i < diff; i++) frag.appendChild(pixelTemplate.cloneNode(true));
      line.appendChild(frag);
    } else if (diff < 0) {
      for (let i = diff; i < 0; i++) line.lastElementChild?.remove();
    }
  });
}

/**
 * Faithful port of runPageLeaveAnimation (+ runPageEnterAnimation's pageReady).
 * @param current the outgoing page (a frozen clone of the previous <main>) — removed at the end.
 * @param next    the incoming, real <main> — clip-revealed on top of `current`.
 * @param onReady called at `pageReady` (transitionDuration + delay) to run resetPage/initPage.
 */
export function runPageTransition(
  current: HTMLElement | null,
  next: HTMLElement | null,
  onReady: () => void
): TransitionHandle {
  // Reduced motion: instant swap, exactly like the source.
  if (reduceMotion()) {
    if (current) current.remove();
    onReady();
    return NOOP_HANDLE;
  }

  const isPortrait = window.innerHeight > window.innerWidth;
  pixelGrid(isPortrait);

  const transitionWrap = document.querySelector<HTMLElement>('[data-transition-wrap]');
  const transitionPanel = transitionWrap?.querySelector<HTMLElement>('[data-transition-panel]');
  if (!transitionPanel || !next) {
    if (current) current.remove();
    onReady();
    return NOOP_HANDLE;
  }

  const lines = Array.from(transitionPanel.querySelectorAll<HTMLElement>('[data-transition-col]'));
  const allPixels = transitionPanel.querySelectorAll<HTMLElement>('[data-transition-pixel]');

  const overlap = Math.max(0, Math.min(1, pixelOverlap));
  const clipFrom = isPortrait
    ? 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)'
    : 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)';
  const clipTo = isPortrait
    ? 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
    : 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
  const clipStart = Math.min(pixelFadeDuration, transitionDuration * 0.5);
  const clipDuration = Math.max(0.001, transitionDuration - 2 * clipStart);
  const stepDur = clipDuration / Math.max(1, pixelHorizontalAmount);
  const transitionEndDelay = transitionDuration / Math.max(1, pixelHorizontalAmount);

  let finished = false;
  const tl = gsap.timeline();

  gsap.set(allPixels, { opacity: 0, willChange: 'opacity' });
  gsap.set(transitionPanel, { opacity: 1, willChange: 'opacity' });

  // `next` is the incoming <main>: pin it over `current` and start it clipped away.
  // (position/zIndex are the Next.js equivalent of Barba's beforeEnter styling;
  // they are cleared by resetPage in `onReady`.)
  gsap.set(next, {
    autoAlpha: 1,
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    clipPath: clipFrom,
    webkitClipPath: clipFrom,
    willChange: 'clip-path',
    force3D: true,
    height: 'var(--rh-stable-viewport-height)',
    maxHeight: 'var(--rh-stable-viewport-height)',
    overflow: 'hidden',
  });

  lines.forEach((line, i) => {
    const pixels = Array.from(line.querySelectorAll<HTMLElement>('[data-transition-pixel]'));
    if (!pixels.length) return;

    const revealTime = clipStart + i * stepDur;
    const fillStart = Math.max(0, revealTime - pixelFadeDuration);
    const fadeStart = Math.min(transitionDuration, revealTime + stepDur);
    const perPixelMin = pixelFadeDuration / pixels.length;
    const perPixelDur = perPixelMin * (1 - overlap) + pixelFadeDuration * overlap;
    const spread = Math.max(0, pixelFadeDuration - perPixelDur);

    // Animate Pixels In
    tl.to(
      pixels,
      { opacity: 1, duration: Math.max(0.001, perPixelDur), ease: 'none', stagger: { amount: spread, from: 'random' } },
      fillStart
    );

    // Animate Pixels Out
    tl.to(
      pixels,
      { opacity: 0, duration: Math.max(0.001, perPixelDur), ease: 'none', stagger: { amount: spread, from: 'random' } },
      fadeStart
    );
  });

  // Animate Clip Path
  tl.to(
    next,
    { clipPath: clipTo, webkitClipPath: clipTo, ease: `steps(${pixelHorizontalAmount}, start)`, duration: clipDuration },
    clipStart
  );

  // The incoming page must be returned to its stylesheet-owned state before
  // page-local GSAP entrances run. In particular, don't retain autoAlpha's
  // inline opacity/visibility from the transition on a future route visit.
  tl.set(
    next,
    { clearProps: 'clipPath,webkitClipPath,willChange,force3D,maxHeight,opacity,visibility' },
    clipStart + clipDuration
  );

  tl.call(
    () => {
      if (current) current.remove();
    },
    undefined,
    transitionDuration + transitionEndDelay
  );

  tl.set(allPixels, { clearProps: 'willChange' }, transitionDuration + transitionEndDelay);
  tl.set(transitionPanel, { clearProps: 'willChange', opacity: 0 }, transitionDuration + transitionEndDelay);

  // pageReady — the point where runPageEnterAnimation resolved and afterEnter ran.
  tl.call(() => {
    finished = true;
    onReady();
  }, undefined, transitionDuration + transitionEndDelay);

  return {
    kill() {
      if (finished) return;
      finished = true;
      tl.kill();
      // Exact mirror of the pin gsap.set() applied to `next` above.
      gsap.set(next, {
        clearProps:
          'position,top,left,right,zIndex,clipPath,webkitClipPath,willChange,force3D,height,maxHeight,overflow,opacity,visibility',
      });
      // Return the overlay to its resting state, matching the timeline's end.
      gsap.set(allPixels, { opacity: 0, clearProps: 'willChange' });
      gsap.set(transitionPanel, { opacity: 0, clearProps: 'willChange' });
      // Deliberately do NOT remove `current` (the frozen clone): the caller
      // keeps pendingFreeze armed so a re-mount replays the wipe over it.
    },
  };
}
